
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import vselect3 from "vselect3";
import VuePerfectScrollbar from "vue-perfect-scrollbar";
import { debounce, get, isEqual, sortBy as _sortBy } from "lodash";
import { validationMixin } from "vuelidate";
import { maxLength, maxValue, minValue, required, requiredIf } from "vuelidate/lib/validators";
import { Getter, namespace, Mutation } from "vuex-class";

import { Response, ResponseAlarmType } from "@/store/responses/types";
import { ServerDetails, FetchServerByGroupParams, SearchServerByGroupParams } from "@/store/devices/types";
import { ServerEventType, LoadServerEventTypesParams, SearchServerEventTypesParams } from "@/store/eventqueue/types";
import { SetupState } from "@/store/setup/types";
import { FeaturesList } from "@/store/types";

import AreaTreeSelect from "@/components/form/AreaTreeSelect.vue";
import { ActionPlan, ActionPlanCategory } from "@/store/tasks/types";
import api from "@/services/api.service";

const Devices = namespace("devices");
const EventQueue = namespace("eventqueue");
const Responses = namespace("responses");
const Setup = namespace("setup");

const debouncePeriod: number = process.env.NODE_ENV === "test" ? 1 : 500;
const AlarmGroupingNone: string = "No Grouping";
const AlarmGroupingResponse: string = "By Response";
const AlarmGroupingDefault: string = "By Area";

@Component({
	components: {
		"area-tree-select": AreaTreeSelect,
		"v-select3": vselect3,
		"vue-perfect-scrollbar": VuePerfectScrollbar
	},
	mixins: [validationMixin],
	validations: {
		currentAlarmEntry: {
			groupID: { required },
			serverID: { required },
			serverTypeEventNum: { required },
			title: { required, maxLength: maxLength(250) },
			input1: { required, minValue: minValue(-1), maxValue: maxValue(2147483647) },
			input2: { minValue: minValue(-1), maxValue: maxValue(2147483647) },
			priority: { required, minValue: minValue(0), maxValue: maxValue(2147483647) },
			extraValue: { maxLength: maxLength(8000) },
			runawayThreshold: { minValue: minValue(0), maxValue: maxValue(2147483647) },
			runawayMinutes: { minValue: minValue(0), maxValue: maxValue(2147483647) },
			matchRadius: { minValue: minValue(0), maxValue: maxValue(2147483647) }
		},
		hasDataChanged: {
			required: requiredIf(function() {
				return isEqual(this.currentAlarm, this.currentAlarmEntry);
			})
		}
	}
})
export default class AlarmSetupModal extends Vue {
	@Getter("getFeaturesList") featuresList: FeaturesList;
	@Mutation private setIsLoading: (loading: boolean) => void;

	@Responses.Action("createNewResponse") createNewAlarm: (alarm: Response) => Promise<boolean>;
	@Responses.Action deleteResponse: (alarmId: number) => Promise<void>;
	@Responses.Action("updateResponse") updateAlarm: (alarm: Response) => Promise<void>;
	@Responses.Mutation setAlarmModalVisible: (isVisible: boolean) => void;
	@Responses.State alarmModalOpened: boolean;
	@Responses.State currentAlarm: Response;
	@Responses.State alarmSetupModalLoaded: boolean;
	@Responses.Mutation setAlarmSetupModalLoaded: (flag: boolean) => void;

	@EventQueue.Action loadServerEventTypes: (params: LoadServerEventTypesParams) => Promise<void>;
	@EventQueue.Action searchServerEventTypes: (params: SearchServerEventTypesParams) => Promise<void>;
	@EventQueue.Mutation setFilteredServerEventTypes: (data: ServerEventType[]) => void;
	@EventQueue.Mutation addServerEventType: (data: ServerEventType) => void;
	@EventQueue.State("ServerEventTypes") storeServerEventTypes: ServerEventType[];
	@EventQueue.State("filteredServerEventTypes") storeFilteredServerEventTypes: ServerEventType[];

	@Devices.Action fetchServersByGroupId: (params: FetchServerByGroupParams) => Promise<void>;
	@Devices.Action searchServersByGroupId: (params: SearchServerByGroupParams) => Promise<void>;
	@Devices.Mutation setFilteredServersList: (data: ServerDetails[]) => void;
	@Devices.Mutation addServer: (data: ServerDetails) => void;
	@Devices.State("serversList") storeDeviceList: ServerDetails[];
	@Devices.State("filteredServersList") storeFilteredDeviceList: ServerDetails[];

	@Setup.Action("groupIdSelected") getGroupIdsToRoot: (groupId: number) => Promise<void>;
	@Setup.State((state: SetupState) => state.currentAreas) private groupIdsToRoot: Set<number>;
	@Setup.State((state: SetupState) => state.responseAlarmTypes) private allResponseAlarmTypes: ResponseAlarmType[];

	private currentAlarmEntry: Response = null;
	private deviceList: ServerDetails[] = [];
	private device: ServerDetails = null;
	private serverEventTypeList: ServerEventType[] = [];
	private deleteConfirmModalOpen: boolean = false;
	private alarmGrouping: string = "";
	private actionPlans: ActionPlan[] = [];
	private areaSelectLoaded: boolean = false;
	private actionPlanCategoryType: ActionPlanCategory = ActionPlanCategory.Alarm;

	private get hasAdvancedAlarmGroupingFlag(): boolean {
		return get(this.featuresList, ["Alarms", "AdvancedAlarmGrouping"]);
	}

	private get isEditing(): boolean {
		return this.currentAlarmEntry.responseID > 0;
	}

	private get modalTitle(): string {
		return this.isEditing ? "Alarm Details" : "Add Alarm";
	}

	private get alarmGroupingOptions(): string[] {
		return [AlarmGroupingNone, AlarmGroupingResponse, AlarmGroupingDefault];
	}

	private get isComponentLoaded(): boolean {
		return this.alarmSetupModalLoaded && this.areaSelectLoaded;
	}

	private get groupIdsToRootAsArray(): number[] {
		return this.groupIdsToRoot ? Array.from(this.groupIdsToRoot) : [];
	}

	private get responseAlarmTypes(): ResponseAlarmType[] {
		return this.allResponseAlarmTypes.filter(rat => this.groupIdsToRootAsArray.includes(rat.groupId));
	}

	@Watch("isComponentLoaded")
	private onComponentLoaded(newValue: boolean) {
		if (newValue) {
			this.setIsLoading(false);
		}
	}

	/** Set state when the entry changes */
	@Watch("currentAlarm")
	private async setCurrentAlarmEntry(): Promise<void> {
		if (!this.currentAlarm || Object.keys(this.currentAlarm).length === 0) {
			this.setIsLoading(false);
		}

		this.alarmGrouping = "";
		this.currentAlarmEntry = { ...this.currentAlarm };

		if (this.currentAlarmEntry.responseID) {
			this.setAlarmGroupingOption();
		}
	}

	/**
	 * Watcher method, handles when the user selects an area for the alarm with the area select.
	 * Fetches the list of devices, server event types, and action plans for the new area.
	 */
	@Watch("currentAlarmEntry.groupID")
	private async onAlarmAreaSelected(): Promise<void> {
		if (!this.currentAlarmEntry.groupID) return;

		this.setIsLoading(true);
		this.setAlarmSetupModalLoaded(false);

		await Promise.all([
			this.fetchServersByGroupId({ groupId: this.currentAlarmEntry.groupID, pageSize: 200 }),
			this.getGroupIdsToRoot(this.currentAlarmEntry.groupID)
		]);
		this.deviceList = this.storeDeviceList;

		if (!this.isEditing) {
			this.device = null;
			await this.populateServerEventTypes();

			//We need to unset this in case the event doesn't exist on the new server type
			if (this.currentAlarmEntry.serverTypeEventNum) {
				this.currentAlarmEntry.serverTypeEventNum = null;
			}
		}

		await this.fetchActionPlans();
		this.setAlarmSetupModalLoaded(true);
	}

	/**
	 * Loads action plans for the currently selected group (area) for the alarm.
	 */
	private async fetchActionPlans() {
		const { data } = await api.loadActionPlans(this.currentAlarmEntry.groupID);
		this.actionPlans = data ? data.filter(a => a.scriptCategoryID === this.actionPlanCategoryType || a.scriptCategoryID === null) : null;		
	}

	/**
	 * Populates the list of server event types, using the currently selected device
	 * and area.
	 */
	private async populateServerEventTypes(): Promise<void> {
		if (!this.device) {
			this.currentAlarmEntry.serverID = null;
			return;
		}

		this.currentAlarmEntry.serverID = this.device.serverID;

		await this.loadServerEventTypes({
			serverType: this.device.serverTypeID,
			groupID: this.currentAlarmEntry.groupID,
			pageSize: 200
		});

		this.setServerEventTypesForDropdown();
	}

	private setServerEventTypesForDropdown(searchTerm: string = null): void {
		const defaultEventTitle: string = "Default";
		let serverEventTypes;

		//copy to new array so we don't mutate original
		if (this.storeFilteredServerEventTypes) {
			serverEventTypes = [...this.storeFilteredServerEventTypes];

			// If there is a search term
			if (searchTerm) {
				// check if it matches / partially matches 'Default' and is so add to the list of results
				if (defaultEventTitle.toLocaleLowerCase().includes(searchTerm.toLocaleLowerCase())) {
					serverEventTypes.splice(0, 0, { eventNum: -1, title: defaultEventTitle } as ServerEventType);
				}
			}
		} else {
			serverEventTypes = [...this.storeServerEventTypes];

			//we need a default option
			serverEventTypes.splice(0, 0, { eventNum: -1, title: "Default" } as ServerEventType);
		}

		//an item may have been added after filtering, so let's sort them
		serverEventTypes = _sortBy(serverEventTypes, "title");

		this.serverEventTypeList = serverEventTypes;
	}

	private setAlarmGroupingOption(): void {
		if (this.currentAlarmEntry.isRaiseIndividual) {
			this.alarmGrouping = AlarmGroupingNone;
		} else if (this.currentAlarmEntry.isRaiseGrouped) {
			this.alarmGrouping = AlarmGroupingResponse;
		} else {
			this.alarmGrouping = AlarmGroupingDefault;
		}
	}

	private async handleDeviceSelected(): Promise<void> {
		//We need to unset this in case the event doesn't exist on the new server type
		if (this.currentAlarmEntry.serverTypeEventNum) {
			this.currentAlarmEntry.serverTypeEventNum = null;
		}

		await this.populateServerEventTypes();
		this.addDeviceToStoreIfRequired();
	}

	private addDeviceToStoreIfRequired(): void {
		if (this.currentAlarmEntry.serverID && !this.deviceExistsInMasterList()) {
			const serverToAdd = this.storeFilteredDeviceList.find(
				(x: ServerDetails) => x.serverID === this.currentAlarmEntry.serverID
			);

			this.addServer({ ...serverToAdd });
		}
	}

	private deviceExistsInMasterList(): boolean {
		return (
			this.storeDeviceList.findIndex((x: ServerDetails) => x.serverID === this.currentAlarmEntry.serverID) !== -1
		);
	}

	private async handleSaveClick(): Promise<void> {
		this.setAlarmModalVisible(false);

		if (this.isEditing) {
			await this.updateAlarm(this.currentAlarmEntry);
			return;
		}

		const serverTypeEvent = this.serverEventTypeList.find(
			set => set.eventNum === this.currentAlarmEntry.serverTypeEventNum
		);

		this.currentAlarmEntry.serverTypeEventTitle = serverTypeEvent.title;
		this.currentAlarmEntry.deviceTitle = this.device.title;

		await this.createNewAlarm(this.currentAlarmEntry);
	}

	private async handleDeleteClick(): Promise<void> {
		this.setAlarmModalVisible(false);
		this.deleteConfirmModalOpen = true;
	}

	private handleModalHidden(): void {
		this.setAlarmSetupModalLoaded(false);
		this.setAlarmModalVisible(false);
	}

	private async handleDeleteConfirmClick(): Promise<void> {
		this.deleteConfirmModalOpen = false;
		await this.deleteResponse(this.currentAlarmEntry.responseID);
	}

	/**
	 * This handler will be called once on delete/cancel, and once by the modal itself.
	 * We need to make sure the contents are only run once.
	 */
	private handleDeleteConfirmModalHidden(): void {
		if (this.deleteConfirmModalOpen) {
			this.setAlarmModalVisible(true);
			this.deleteConfirmModalOpen = false;
		}
	}

	private handleAlarmGroupingSelected(): void {
		switch (this.alarmGrouping) {
			case AlarmGroupingNone:
				this.currentAlarmEntry.isRaiseIndividual = true;
				this.currentAlarmEntry.isRaiseGrouped = false;
				break;
			case AlarmGroupingResponse:
				this.currentAlarmEntry.isRaiseIndividual = false;
				this.currentAlarmEntry.isRaiseGrouped = true;
				break;
			default:
				this.currentAlarmEntry.isRaiseIndividual = false;
				this.currentAlarmEntry.isRaiseGrouped = false;
		}
	}

	private setDevicesForDropdown(): void {
		//copy to new array so we don't mutate original
		let devices = this.storeFilteredDeviceList ? [...this.storeFilteredDeviceList] : [...this.storeDeviceList];

		//an item may have been added after filtering, so let's sort them
		devices = _sortBy(devices, "title");

		this.deviceList = devices;
	}

	private handleDeviceSearch(searchTerm: string, loading: (boolean) => void): void {
		loading(true);
		this.debounceSearch(searchTerm, loading, "Servers");
	}

	private handleServerTypeEventSearch(searchTerm: string, loading: (boolean) => void): void {
		loading(true);
		this.debounceSearch(searchTerm, loading, "ServerTypeEvents");
	}

	private debounceSearch = debounce(
		async (searchTerm: string, loading: (boolean) => void, filterType: string): Promise<void> => {
			filterType === "Servers"
				? await this.searchServers(searchTerm, loading)
				: await this.searchServerTypeEvents(searchTerm, loading);
		},
		debouncePeriod
	);

	private async searchServers(searchTerm: string, loading: (boolean) => void): Promise<void> {
		if (!searchTerm) {
			this.setFilteredServersList(null);
		} else {
			//We need to unset this in case the device doesn't exist in the new list
			if (this.device) {
				this.device = null;
			}

			await this.searchServersByGroupId({ groupId: this.currentAlarmEntry.groupID, filter: searchTerm });
		}

		this.setDevicesForDropdown();

		loading(false);
	}

	private async searchServerTypeEvents(searchTerm: string, loading: (boolean) => void): Promise<void> {
		if (!searchTerm) {
			this.setFilteredServerEventTypes(null);
		} else {
			//We need to unset this in case the event doesn't exist in the new list
			if (this.currentAlarmEntry.serverTypeEventNum) {
				this.currentAlarmEntry.serverTypeEventNum = null;
			}

			await this.searchServerEventTypes({
				serverType: this.device.serverTypeID,
				groupID: this.currentAlarmEntry.groupID,
				filter: searchTerm
			});
		}

		this.setServerEventTypesForDropdown(searchTerm);

		loading(false);
	}

	private async handleServerTypeEventSelected(): Promise<void> {
		this.addServerTypeEventToStoreIfRequired();
	}

	private addServerTypeEventToStoreIfRequired(): void {
		if (this.isEditing) return;

		if (this.currentAlarmEntry.serverTypeEventNum && !this.serverTypeEventExistsInMasterList()) {
			const serverEventTypeToAdd = this.storeFilteredServerEventTypes.find(
				(x: ServerEventType) => x.eventNum === this.currentAlarmEntry.serverTypeEventNum
			);

			this.addServerEventType({ ...serverEventTypeToAdd });
		}
	}

	private serverTypeEventExistsInMasterList(): boolean {
		return (
			this.storeServerEventTypes.findIndex(
				(x: ServerEventType) => x.eventNum === this.currentAlarmEntry.serverTypeEventNum
			) !== -1
		);
	}

	private numberValidationErrorString(condition: boolean, min: number): string {
		if (condition) {
			return `The value must be between ${min} and 2147483647`;
		}
		return "";
	}

	// This should only ever run once
	private areaSelectedLoaded(): void {
		this.areaSelectLoaded = true;
	}
}
